<script>
import parsePhoneNumber from "libphonenumber-js";
import {first, isNil, merge} from "lodash";
import CallIcon from '../svg/CallIcon.vue'
import PointerIcon from "../svg/PointerIcon.vue";
import MailIcon from "../svg/MailIcon.vue";
import ArrowIcon from "../svg/ArrowIcon.vue";
import AppointmentStore from "../../mixins/appointment-store";

export default {
    name: "GarageRow",
    components: {ArrowIcon, MailIcon, PointerIcon, CallIcon},

    props: {
        garage: {
            type: Object,
            required: true,
        },
        initialGarage: {
            type: Boolean,
            default: false
        }
    },

    mixins: [AppointmentStore],

    data() {
        return {
            openText: window.Drupal.t('Open today:'),
            closedText: window.Drupal.t('Closed'),
            chosenDate: [],
        }
    },

    computed: {
        isMobile() {
            return window.matchMedia('(max-width: 767px)').matches;
        },

        calendarColumns() {
            return this.isMobile ? 1 : 2;
        },

        isOpen() {
            if (!this.garage.opening_hours) {
                return false;
            }

            return this.garage.opening_hours.isOpen;
        },

        currentSlot() {
            if (!this.garage.opening_hours) {
                return false;
            }

            let activeDay = _.find(this.garage.opening_hours.days, (day) => {
                return day.active === true;
            });

            if (_.isEmpty(activeDay.slots)) {
                return false;
            }

            return `${_.first(activeDay.slots).open} - ${_.last(activeDay.slots).closed}`;
        },

        googleLink() {
            return `https://www.google.com/maps/search/?api=1&query=${this.garage.geolocation.lat},${this.garage.geolocation.lng}`
        },

        parsedPhonenumber() {
            if (_.isEmpty(this.garage.tel)) {
                return;
            }

            return parsePhoneNumber(this.garage.tel, 'NL').number;
        },

        active() {
            return this.appointmentStore.activeGarage?.id === this.garage.id
        },

        minDate() {
            const d = new Date();
            d.setDate(d.getDate() + 7);
            return d;
        },

        readyForNextStep() {
            return this.chosenDate.length > 0 && this.appointmentStore.time !== '';
        },

        isRequestOffer() {
            return this.appointmentStore.isRequestOffer
        },

        disabledDates() {
            const days = this.garage?.opening_hours?.days ?? {};

            const disabledWeekdays = Object.entries(days)
                .filter(([, day]) => isNil(day.slots))
                .map(([key]) => (Number(key) % 7) + 1);

            const explicitDates = (this.appointmentStore.blockedDates ?? []).map(d => d.value);

            return [
                { weekdays: disabledWeekdays },
                ...explicitDates,
            ];
        }
    },

    methods: {
        handleClick() {
            if (this.garage.plannerUrl) {
                window.location = `${this.garage.url}/afspraak-maken`
                return;
            }

            if (this.active) {
                this.appointmentStore.setActiveGarage(null)

                return;
            }

            this.appointmentStore.setActiveGarage(this.garage)
        },

        handleOnClickDay(day) {
            if (day.isDisabled) {
                return;
            }

            if (day.date === first(this.chosenDate)?.dates) {
                this.chosenDate = [];
                this.appointmentStore.date = null;

                return;
            }

            this.appointmentStore.date = day.date;

            this.chosenDate = [{
                highlight: true,
                dates: day.date
            }];
        },

        handleClickChooseGarage() {
            this.appointmentStore.setActiveGarage(this.garage)
            this.appointmentStore.goToStep(3)
        }
    }
}
</script>

<template>
<div :class="{'vue-garage-row': true, 'active': active }">
   <div class="upper-section">
       <h2 class="virtual-h4 mb-2">CarProf {{ garage.title }}</h2>
       <p class="mb-0 font-weight-bold">{{ garage.streetNumber }}, {{ garage.postalCodePlace }}</p>

       <p :class="{
    'office-hours-mini mb-2': true,
    'is-open': isOpen && currentSlot
  }" >
           <template v-if="isOpen && currentSlot">
               {{ openText }}
               {{ currentSlot }}
           </template>

           <template v-else>
               {{ closedText }}
           </template>
       </p>

       <div class="button-wrapper justify-content-between">
          <div class="button-wrapper">
              <a class="btn-arrow-inverse" :href="googleLink" target="_blank"><PointerIcon/> Route</a>

              <a class="btn-arrow-inverse" v-if="parsedPhonenumber" :href="`tel:${parsedPhonenumber}`"><CallIcon/><span class="visually-hidden"> {{ garage.tel }}</span></a>

              <a class="btn-arrow-inverse" v-if="garage.email" :href="`mailto:${garage.email}`"><MailIcon/><span class="visually-hidden">{{ garage.email }}</span></a>
          </div>

           <button class="btn-arrow-inverse" @click="handleClick" v-if="!initialGarage && !isRequestOffer">
               Selecteer vestiging en datum <span :class="{'active': active}"><ArrowIcon/></span>
           </button>

           <button class="btn-arrow-inverse" @click="handleClickChooseGarage" v-if="isRequestOffer">
               Kies vestiging
           </button>
       </div>
   </div>

    <div class="lower-section" v-if="active && !isRequestOffer">
        <hr>
        <h3 class="virtual-h4 mb-2">1. Kies uw beschikbaarheid</h3>
        <p>Selecteer eerst een dag die u het beste uitkomt en daarna een tijd waarop u langs wil komen voor een afspraak.</p>

        <vc-calendar
            :columns.number="calendarColumns"
            :min-date="minDate"
            locale="nl"
            @dayclick="handleOnClickDay"
            :attributes="chosenDate"
            :disabled-dates="disabledDates"
            class="mb-5"
        ></vc-calendar>

        <h3 class="virtual-h4 mb-2">2. Kies een voorkeursmoment</h3>

        <div class="button-wrapper mb-5">
            <button @click="appointmentStore.time = 'morning'" :class="[appointmentStore.time === 'morning' ? 'btn-arrow' : 'btn-arrow-inverse']">Ochtend</button>
            <button @click="appointmentStore.time = 'afternoon'" :class="[appointmentStore.time === 'afternoon' ? 'btn-arrow' : 'btn-arrow-inverse']">Middag</button>
        </div>

        <button @click="appointmentStore.goToStep(3)" class="btn-arrow" type="button" :disabled="!readyForNextStep">Stap 3 - Services kiezen</button>
    </div>
</div>
</template>